import { parseStart } from './hrModel.js';
import { cleanFacility, readRepText as readText } from './repText.js';
import { MAX_REP_BYTES } from './studyFiles.js';

export const REP_PARSER_VERSION = 3;

const empty = () => ({ facility: '', patient: '', study: '', gender: '', age: '', start: '' });
function readDate(view, offset) {
  const year = view.getUint16(offset, true);
  const month = view.getUint8(offset + 2);
  const day = view.getUint8(offset + 3);
  const hour = view.getUint8(offset + 4);
  const minute = view.getUint8(offset + 5);
  const second = view.getUint8(offset + 6);
  if (year < 1980 || year > 2199 || month < 1 || month > 12 || day < 1 || day > 31) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const value = `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}`;
  try {
    parseStart(value);
    return value;
  } catch {
    return '';
  }
}

export function parseRep(buffer, study) {
  if (buffer.byteLength > MAX_REP_BYTES) throw new Error('REP file exceeds 1 MiB.');
  const meta = empty();
  const unsupported = () => ({
    parserVersion: REP_PARSER_VERSION,
    meta,
    recognized: false,
    warnings: [
      'REP layout is not recognized. Enter the header fields and recording start time manually; no demographics were guessed.',
    ],
  });
  // Only the layout established in the supplied study is supported. Do not scan arbitrary binary strings for names.
  if (![24832, 25601].includes(buffer.byteLength)) return unsupported();
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  const device = readText(bytes, 256, 32);
  const repeatedDevice = readText(bytes, 24576, 32);
  const firstStart = readDate(view, 384);
  const repeatedStart = readDate(view, 24704);
  // Fixed-width ISO timestamps sort chronologically. Use the newest valid field.
  const start = [firstStart, repeatedStart].filter(Boolean).sort().at(-1) || '';
  if (!device || device !== repeatedDevice || !start) return unsupported();
  // Same device is not proof of same session, but a mismatch rules the selection out.
  if (
    !study.toLowerCase().endsWith(`_${device.toLowerCase()}`) &&
    study.toLowerCase() !== device.toLowerCase()
  )
    throw new Error('REP recording identifier does not match the selected study filename.');
  const warnings = [
    'Demographics use the observed ARCS REP layout. Verify the imported header against the study.',
  ];
  // User-confirmed Study ID: null-terminated text in the observed REP layout.
  const storedStudy = readText(bytes, 24352, 12);
  const nameField = readText(bytes, 24320, 20).replace(/,+$/, '').trim();
  const studyIdCandidate = /^\d{10}$/.test(nameField) ? nameField : '';
  let studyIdWarning = '';
  if (/^\d{10}$/.test(storedStudy)) meta.study = storedStudy;
  else if (storedStudy) {
    studyIdWarning = `REP Study ID field contains "${storedStudy}"; expected a ten-digit report Study ID. Confirm and enter the correct ID manually.`;
  } else if (studyIdCandidate) {
    studyIdWarning = `REP Study ID field is empty. Possible ID ${studyIdCandidate} appears in the name field; confirm it against the PDF and enter it manually. It was not used for automatic matching.`;
  } else {
    studyIdWarning =
      bytes[24352] === 0
        ? 'REP Study ID field is empty. Enter the confirmed ID manually or use an export containing the ID.'
        : 'REP Study ID field is unreadable. Enter the confirmed ID manually or use another export.';
  }
  if (studyIdWarning) warnings.push(studyIdWarning);
  meta.start = start;
  meta.facility = cleanFacility(readText(bytes, 23040, 32));
  const last = studyIdCandidate ? '' : nameField;
  const first = readText(bytes, 24720, 128);
  meta.patient = last && first ? `${last}, ${first}` : last || first;
  const recordedAge = readText(bytes, 24340, 3);
  if (/^\d{1,3}$/.test(recordedAge) && +recordedAge <= 120) meta.age = String(+recordedAge);
  const dob = String.fromCharCode(...bytes.subarray(24448, 24456));
  let dateOfBirth = '';
  if (/^\d{8}$/.test(dob)) {
    const value = `${dob.slice(0, 4)}-${dob.slice(4, 6)}-${dob.slice(6, 8)}`;
    try {
      const born = parseStart(`${value}T00:00`);
      const studyDate = parseStart(start);
      const yearDifference = +start.slice(0, 4) - +dob.slice(0, 4);
      const age = yearDifference - (start.slice(5, 10) < value.slice(5, 10) ? 1 : 0);
      if (born <= studyDate && age >= 0 && age <= 120) {
        dateOfBirth = value;
        if (!meta.age) meta.age = String(age);
        else if (+meta.age !== age)
          warnings.push(
            'Stored age differs from age calculated from the apparent date of birth. Check the age field.'
          );
      }
    } catch {
      /* Unverified birth date is ignored; never use a malformed date. */
    }
  }
  // Confirmed REP mapping: 0 = male, 1 = female; all other codes are blank.
  if (bytes[24343] === 0) meta.gender = 'M';
  else if (bytes[24343] === 1) meta.gender = 'F';
  if (!meta.facility) warnings.push('Facility was not available; enter your customer heading.');
  if (!meta.patient) warnings.push('Patient name was not available; enter it manually.');
  return {
    meta,
    recognized: true,
    warnings,
    dateOfBirth,
    studyIdWarning,
    studyIdCandidate,
    parserVersion: REP_PARSER_VERSION,
  };
}
